<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, ButtonDropdown, Col, Container, Row } from '@sveltestrap/sveltestrap';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';

  import { DomainCard } from '@client/components/domain-card';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { Loader } from '@client/components/loader';
  import { Pagination } from '@client/components/pagination';

  import { DomainService } from '@client/services/domain';
  import { DomainsService } from '@client/services/domains';

  import { domainsSettingsStore } from '@client/stores/page-settings';
  import { userStore } from '@client/stores/user';

  import { QueryParams } from '@client/utils/query-params';

  import { STAFF_REQUEST_PARAMS } from '@client/enums';

  import { debounce } from '@common/utils/debounce';
  import { deepCopy, merge } from '@common/utils/objects';

  const { basepath } = getContext('config');

  const queryClient = useQueryClient();

  let showFilters = true;
  let showCreateDomainModal = false;
  let showManageMDMAccountsModal = false;

  let domains = [];
  let results = 0;
  let totalRecords = 0;
  let queryParams = deepCopy(merge({}, $domainsSettingsStore.params || { ...STAFF_REQUEST_PARAMS }));

  let searchQueryParam = queryParams.query;
  let selectedSearchField = 'query';
  let searchInputValue = QueryParams.get('search') || queryParams.query || '';

  let search = QueryParams.get('search');

  queryParams.page = search?.length ? 1 : QueryParams.get('page') || queryParams.page;
  queryParams.perPage = QueryParams.get('perpage') || queryParams.perPage;

  const updateQueryParamsAndRefetch = () => {
    queryClient.setQueryData(['domains', queryParams], (oldData) => ({
      ...oldData,
      ...queryParams
    }));

    queryClient.invalidateQueries(['domains', queryParams]);
  };

  const updateQueryParams = (params) => {
    queryParams = { ...params };
    $domainsSettingsStore.params = queryParams;
    updateQueryParamsAndRefetch();
  };

  const onChangePage = (page) => updateQueryParams({ ...queryParams, page });
  const onChangeRecords = (perPage) => updateQueryParams({ ...queryParams, perPage });

  const onChangeSearch = debounce((value) => {
    queryClient.cancelQueries(['domains', queryParams]);

    const params = {
      query: '',
      billingid: ''
    };

    const trimmedValue = value.trim();
    params.query = trimmedValue;

    updateQueryParams({ ...queryParams, ...params, page: 1 });
  }, 500);

  let usageData = {};

  const getEBCData = async () => {
    const response = await DomainService.getEBC();

    if (response && response?.billingidSeats) {
      usageData = response.billingidSeats['60'] || {};
    }

    return response;
  };

  const queryFn = async () => {
    queryParams.screen = '';

    const response = await DomainsService.getAll(queryParams);

    if (response.status === 500) {
      domains = [];
      results = 0;
      totalRecords = 0;
      return { data: [], headers: {} };
    }

    domains = response.data || [];
    results = (response.headers && response.headers['x-search-total']) || 0;
    totalRecords = (response.headers && response.headers['x-filtered-total']) || domains.length;

    return response;
  };

  updateQueryParams(queryParams);

  const query = createQuery({ queryKey: ['domains', queryParams], queryFn });

  // ebcdata is a staff-only backend report; it 404s by design for customer admins
  // (mailapi/models/user.js). Only staff can populate the usage column, so skip the
  // request otherwise instead of firing a guaranteed 404 on every customer's page load.
  if ($userStore.isStaff) {
    getEBCData();
  }
</script>

<div id="domains" class="page" class:with-footer={$query.data?.data}>
  <Loader backdrop show={$query.isLoading || $query.isFetching} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.DOMAINS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col md="12">
          <h1 class="page-title">{$format('label.MANAGE_DOMAINS')}</h1>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="toolbar">
      <Container fluid>
        <Row>
          <Col lg="8" md="6" sm="12">
            <FilterBar bind:show={showFilters}>
              <div class="toolbar-left" class:collapsed={!showFilters}>
                <SearchFilter bind:search={searchInputValue} onChange={onChangeSearch} />
              </div>
            </FilterBar>
          </Col>
          <Col lg="4" md="6" sm="12">
            <div class="toolbar-right">
              {#if domains.length !== results}
                <small class="count">
                  {$format('label.MATCHES_X_OF_X_DOMAINS', {
                    values: {
                      filtered: Number(totalRecords).toLocaleString(),
                      total: Number(results).toLocaleString()
                    }
                  })}
                </small>
              {/if}
            </div>
          </Col>
        </Row>
      </Container>
    </div>

    {#if domains}
      <div class="domain-cards">
        <Container fluid>
          <Row>
            <Col>
              <div class="horizontal-cards">
                {#each domains as domain}
                  <DomainCard
                    {domain}
                    selected={$userStore.domain === domain.domainname}
                    bind:usage={usageData[domain?.domainsettings?.billing_id]}
                  />
                {/each}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    {/if}
  </div>

  {#if domains.length}
    <div class="page-footer">
      <Pagination
        bind:currentPage={queryParams.page}
        bind:perPage={queryParams.perPage}
        {totalRecords}
        {onChangeRecords}
        {onChangePage}
      />
    </div>
  {/if}
</div>

<style lang="scss" src="./domains.scss"></style>
